import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, PhoneCall, FileSpreadsheet, Search, BarChart3, Target, Trophy } from 'lucide-react';
import { USAMap } from '../components/USAMap';
import { LoginScreen } from '../components/admin/LoginScreen';
import { CallingMode } from '../components/admin/CallingMode';
import { ImportModal } from '../components/admin/ImportModal';
import { AddDealModal, EditDealModal } from '../components/admin/DealModals';
import { EditStageModal, AddStageModal } from '../components/admin/StageModals';
import { KanbanBoard } from '../components/admin/KanbanBoard';
import { PipelineChart } from '../components/admin/PipelineChart';
import { WeeklyCalendar } from '../components/admin/WeeklyCalendar';
import { PlanCalculator } from '../components/admin/PlanCalculator';
import { DoomsdayClock } from '../components/admin/DoomsdayClock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem('admin_token'));
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [deals, setDeals] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newDeal, setNewDeal] = useState({ restaurant_name: '', phone: '', contact: '', deal_value: '', stage: '', state: '', email: '', street_address: '', zipcode: '' });
  const [dragDeal, setDragDeal] = useState(null);
  const [editDeal, setEditDeal] = useState(null);
  const [dailyLeads, setDailyLeads] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState('url');
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheetPreview, setSheetPreview] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [importStage, setImportStage] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');
  const [overrideState, setOverrideState] = useState('');
  const [overrideDealValue, setOverrideDealValue] = useState('');
  const [callMode, setCallMode] = useState(false);
  const [callQueue, setCallQueue] = useState([]);
  const [callIndex, setCallIndex] = useState(0);
  const [callSourceStage, setCallSourceStage] = useState('');
  const [callValue, setCallValue] = useState(() => sessionStorage.getItem('call_value') || '');
  const [callsMade, setCallsMade] = useState(() => parseInt(localStorage.getItem('calls_made') || '0'));
  const [stages, setStages] = useState([]);
  const [stateFilter, setStateFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showARR, setShowARR] = useState(true);
  const [showStageEdit, setShowStageEdit] = useState(null);
  const [goalRevenue, setGoalRevenue] = useState(() => sessionStorage.getItem('goal_revenue') || '');
  const [todayCalls, setTodayCalls] = useState(() => sessionStorage.getItem('today_calls') || '');
  const [callLog, setCallLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem('call_log') || '[]'); } catch { return []; }
  });
  const [showChart, setShowChart] = useState(false);
  const [stageEditName, setStageEditName] = useState('');
  const [stageEditColor, setStageEditColor] = useState('');
  const [showAddStage, setShowAddStage] = useState(false);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#FFD700');
  const [callNote, setCallNote] = useState('');
  const [goalDeadline, setGoalDeadline] = useState(() => sessionStorage.getItem('goal_deadline') || '');
  const [excludeWeekends, setExcludeWeekends] = useState(() => sessionStorage.getItem('exclude_weekends') === 'true');

  const STAGES = stages.map(s => s.name);
  const STAGE_COLORS = {};
  stages.forEach(s => { STAGE_COLORS[s.name] = s.color; });

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // --- Data fetching ---
  const fetchDeals = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/deals`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { setToken(null); sessionStorage.removeItem('admin_token'); return; }
      const data = await res.json();
      setDeals(data);
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchStages = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/stages`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { setToken(null); sessionStorage.removeItem('admin_token'); return; }
      const data = await res.json();
      if (Array.isArray(data)) setStages(data);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchDeals(); fetchStages(); }, [fetchDeals, fetchStages]);

  useEffect(() => {
    if (stages.length > 0 && !callSourceStage) setCallSourceStage(stages[0].name);
    if (stages.length > 0 && !importStage) setImportStage(stages[0].name);
  }, [stages, callSourceStage, importStage]);

  // --- Auth ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) { setLoginError('Wrong password. Access denied.'); return; }
      const data = await res.json();
      setToken(data.token);
      sessionStorage.setItem('admin_token', data.token);
    } catch (e) { setLoginError('Connection error.'); }
  };

  // --- Deal CRUD ---
  const handleAddDeal = async (e) => {
    e.preventDefault();
    if (!newDeal.restaurant_name || !newDeal.deal_value) return;
    await fetch(`${API_URL}/api/admin/deals`, {
      method: 'POST', headers,
      body: JSON.stringify({ ...newDeal, stage: newDeal.stage || STAGES[0] || 'Cold Call', deal_value: parseFloat(newDeal.deal_value) || 0 }),
    });
    setNewDeal({ restaurant_name: '', phone: '', contact: '', deal_value: '', stage: STAGES[0] || '', state: '', email: '', street_address: '', zipcode: '' });
    setShowAdd(false);
    fetchDeals();
  };

  const handleUpdateDeal = async () => {
    if (!editDeal) return;
    await fetch(`${API_URL}/api/admin/deals/${editDeal.id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({
        restaurant_name: editDeal.restaurant_name, phone: editDeal.phone,
        contact: editDeal.contact, deal_value: parseFloat(editDeal.deal_value) || 0,
        stage: editDeal.stage, state: editDeal.state || '', notes: editDeal.notes || '',
        email: editDeal.email || '', street_address: editDeal.street_address || '', zipcode: editDeal.zipcode || '',
      }),
    });
    setEditDeal(null);
    fetchDeals();
  };

  const handleDeleteDeal = async (id) => {
    await fetch(`${API_URL}/api/admin/deals/${id}`, { method: 'DELETE', headers });
    setEditDeal(null);
    fetchDeals();
  };

  const handleDeleteStageDeals = async (stage) => {
    if (!window.confirm(`Delete ALL deals in "${stage}"? This cannot be undone.`)) return;
    await fetch(`${API_URL}/api/admin/deals/stage/${encodeURIComponent(stage)}`, { method: 'DELETE', headers });
    fetchDeals();
  };

  // --- Deal drag ---
  const handleDragStart = (deal) => setDragDeal(deal);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (stage) => {
    if (!dragDeal || dragDeal.stage === stage) { setDragDeal(null); return; }
    await fetch(`${API_URL}/api/admin/deals/${dragDeal.id}`, { method: 'PUT', headers, body: JSON.stringify({ stage }) });
    setDragDeal(null);
    fetchDeals();
  };
  const handleTouchStart = (deal) => setDragDeal(deal);
  const handleTouchEnd = async (e) => {
    if (!dragDeal) return;
    const touch = e.changedTouches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const stageCol = el?.closest('[data-stage]');
    if (stageCol) {
      const stage = stageCol.getAttribute('data-stage');
      if (stage && stage !== dragDeal.stage) {
        await fetch(`${API_URL}/api/admin/deals/${dragDeal.id}`, { method: 'PUT', headers, body: JSON.stringify({ stage }) });
        fetchDeals();
      }
    }
    setDragDeal(null);
  };

  const quickMove = async (deal, newStage) => {
    if (deal.stage === newStage) return;
    await fetch(`${API_URL}/api/admin/deals/${deal.id}`, { method: 'PUT', headers, body: JSON.stringify({ stage: newStage }) });
    fetchDeals();
  };

  // --- Column reorder ---
  const stampEmail = async (deal) => {
    const now = new Date().toISOString();
    await fetch(`${API_URL}/api/admin/deals/${deal.id}`, { method: 'PUT', headers, body: JSON.stringify({ last_emailed: now }) });
    setDeals(prev => prev.map(d => d.id === deal.id ? { ...d, last_emailed: now } : d));
  };

  const moveGroup = async (groupDeals, newStage) => {
    setDeals(prev => prev.map(d => groupDeals.some(gd => gd.id === d.id) ? { ...d, stage: newStage } : d));
    await Promise.all(groupDeals.map(d =>
      fetch(`${API_URL}/api/admin/deals/${d.id}`, { method: 'PUT', headers, body: JSON.stringify({ stage: newStage }) })
    ));
  };

  const moveColumn = async (stageName, direction) => {
    const idx = STAGES.indexOf(stageName);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= STAGES.length) return;
    const reordered = [...stages];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    const updated = reordered.map((s, i) => ({ ...s, order: i }));
    setStages(updated);
    await fetch(`${API_URL}/api/admin/stages/reorder`, { method: 'PUT', headers, body: JSON.stringify({ stages: updated.map(s => s.name) }) });
  };

  const moveDealInColumn = async (dealId, stage, direction) => {
    const stageDeals = deals.filter(d => d.stage === stage).sort((a, b) => {
      const pa = a.priority ?? 999999;
      const pb = b.priority ?? 999999;
      if (pa !== pb) return pa - pb;
      return (a.restaurant_name || '').localeCompare(b.restaurant_name || '');
    });
    const idx = stageDeals.findIndex(d => d.id === dealId);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= stageDeals.length) return;
    const reordered = [...stageDeals];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    const updatedDeals = deals.map(d => {
      const ri = reordered.findIndex(r => r.id === d.id);
      if (ri >= 0) return { ...d, priority: ri };
      return d;
    });
    setDeals(updatedDeals);
    await fetch(`${API_URL}/api/admin/deals/stage/${encodeURIComponent(stage)}/reorder`, {
      method: 'PUT', headers, body: JSON.stringify({ deal_ids: reordered.map(d => d.id) }),
    });
  };

  // --- Filtering ---
  const allStates = [...new Set(deals.map(d => d.state).filter(Boolean))].sort();
  const filteredDeals = deals.filter(d => {
    if (stateFilter && d.state !== stateFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (d.restaurant_name || '').toLowerCase().includes(q) ||
             (d.phone || '').toLowerCase().includes(q) ||
             (d.contact || '').toLowerCase().includes(q) ||
             (d.state || '').toLowerCase().includes(q);
    }
    return true;
  });
  const getStageDeals = (stage) => filteredDeals.filter(d => d.stage === stage).sort((a, b) => {
    const pa = a.priority ?? 999999;
    const pb = b.priority ?? 999999;
    if (pa !== pb) return pa - pb;
    return (a.restaurant_name || '').localeCompare(b.restaurant_name || '');
  });
  const getStageTotal = (stage) => getStageDeals(stage).reduce((sum, d) => sum + (d.deal_value || 0), 0);
  const exportStageCSV = (stage) => {
    const rows = getStageDeals(stage);
    if (!rows.length) return;
    const cols = ['restaurant_name', 'phone', 'contact', 'email', 'street_address', 'state', 'zipcode', 'deal_value', 'stage', 'last_called', 'last_emailed', 'notes', 'created_at'];
    const header = ['Restaurant Name', 'Phone', 'Contact', 'Email', 'Street Address', 'State', 'Zip Code', 'Deal Value', 'Stage', 'Last Called', 'Last Emailed', 'Notes', 'Created At'];
    const csv = [header.join(','), ...rows.map(d => cols.map(c => {
      const v = String(d[c] ?? '');
      return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${stage.replace(/\s+/g, '_')}_${rows.length}_deals.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const totalDeals = filteredDeals.length;
  const totalARR = filteredDeals.reduce((sum, d) => sum + ((d.deal_value || 0) * 12), 0);
  const totalMRR = filteredDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0);

  // --- Import ---
  const handlePreviewSheet = async () => {
    setImportError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/preview-sheet`, { method: 'POST', headers, body: JSON.stringify({ url: sheetUrl }) });
      const data = await res.json();
      if (!res.ok) { setImportError(typeof data.detail === 'string' ? data.detail : 'Failed to fetch sheet'); return; }
      setSheetPreview(data); setColumnMap({}); setImportStep('map');
    } catch (e) { setImportError('Connection error'); }
  };
  const handleImportSheet = async () => {
    setImportStep('importing'); setImportError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/import-sheet`, {
        method: 'POST', headers,
        body: JSON.stringify({ url: sheetUrl, column_map: columnMap, stage: importStage, override_state: overrideState, override_deal_value: overrideDealValue ? parseFloat(overrideDealValue) : null }),
      });
      const data = await res.json();
      if (!res.ok) { setImportError(typeof data.detail === 'string' ? data.detail : 'Import failed'); setImportStep('map'); return; }
      setImportResult(data); setImportStep('done'); fetchDeals();
    } catch (e) { setImportError('Connection error'); setImportStep('map'); }
  };
  const resetImport = () => {
    setShowImport(false); setImportStep('url'); setSheetUrl('');
    setSheetPreview(null); setColumnMap({}); setImportResult(null); setImportError('');
    setOverrideState(''); setOverrideDealValue('');
  };

  // --- Call mode ---
  const startCallMode = (stage) => {
    if (!stage) return;
    const queue = filteredDeals.filter(d => d.stage === stage).sort((a, b) => (a.restaurant_name || '').localeCompare(b.restaurant_name || ''));
    if (queue.length === 0) return;
    setCallQueue(queue); setCallIndex(0); setCallNote(queue[0]?.notes || ''); setCallSourceStage(stage); setCallMode(true);
  };
  const incrementCalls = () => {
    setCallsMade(prev => { const next = prev + 1; localStorage.setItem('calls_made', String(next)); return next; });
  };
  const resetCallCounter = () => { setCallsMade(0); localStorage.setItem('calls_made', '0'); };
  const handleCallAction = async (newStage) => {
    const deal = callQueue[callIndex];
    const updates = { last_called: new Date().toISOString() };
    if (deal.stage !== newStage) updates.stage = newStage;
    if (callNote !== (deal.notes || '')) updates.notes = callNote;
    fetch(`${API_URL}/api/admin/deals/${deal.id}`, { method: 'PUT', headers, body: JSON.stringify(updates) });
    incrementCalls();
    if (callIndex < callQueue.length - 1) { const ni = callIndex + 1; setCallIndex(ni); setCallNote(callQueue[ni]?.notes || ''); }
    else { setCallMode(false); setCallNote(''); fetchDeals(); }
  };
  const skipCall = () => {
    const deal = callQueue[callIndex];
    if (callNote !== (deal.notes || '')) fetch(`${API_URL}/api/admin/deals/${deal.id}`, { method: 'PUT', headers, body: JSON.stringify({ notes: callNote }) });
    incrementCalls();
    if (callIndex < callQueue.length - 1) { const ni = callIndex + 1; setCallIndex(ni); setCallNote(callQueue[ni]?.notes || ''); }
    else { setCallMode(false); setCallNote(''); fetchDeals(); }
  };
  const exitCallMode = () => { setCallMode(false); fetchDeals(); };

  // --- Stage CRUD ---
  const handleAddStage = async () => {
    if (!newStageName.trim()) return;
    await fetch(`${API_URL}/api/admin/stages`, { method: 'POST', headers, body: JSON.stringify({ name: newStageName.trim(), color: newStageColor }) });
    setNewStageName(''); setNewStageColor('#FFD700'); setShowAddStage(false); fetchStages();
  };
  const handleUpdateStage = async () => {
    if (!showStageEdit || !stageEditName.trim()) return;
    await fetch(`${API_URL}/api/admin/stages/${encodeURIComponent(showStageEdit)}`, { method: 'PUT', headers, body: JSON.stringify({ name: stageEditName.trim(), color: stageEditColor }) });
    setShowStageEdit(null); fetchStages(); fetchDeals();
  };
  const handleDeleteStage = async (name) => {
    if (!window.confirm(`Delete column "${name}"? Deals in this column will NOT be deleted.`)) return;
    await fetch(`${API_URL}/api/admin/stages/${encodeURIComponent(name)}`, { method: 'DELETE', headers }); fetchStages();
  };

  // --- LOGIN ---
  if (!token) return <LoginScreen password={password} setPassword={setPassword} loginError={loginError} handleLogin={handleLogin} />;

  // --- CRM DASHBOARD ---
  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#fff' }}>
      <style>{`
        .crm-header { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; padding: 0.75rem 1rem; }
        .crm-header-left { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
        .crm-header-right { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin-left: auto; }
        .crm-stats-bar { display: flex; flex-wrap: wrap; gap: 0.5rem 0.75rem; align-items: center; padding: 0.5rem 1rem; }
        .crm-stats-bar .divider { display: none; }
        .crm-filter-bar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; padding: 0.5rem 1rem; }
        @media (min-width: 1024px) {
          .crm-header { padding: 1rem 1.5rem; flex-wrap: nowrap; }
          .crm-stats-bar { padding: 0.75rem 1.5rem; gap: 1rem; }
          .crm-stats-bar .divider { display: block; }
          .crm-filter-bar { padding: 0.5rem 1.5rem; gap: 1rem; }
        }
        @media (max-width: 768px) {
          .crm-header-right { margin-left: 0; width: 100%; overflow-x: auto; }
          .crm-goal-section { width: 100%; margin-left: 0 !important; }
        }
      `}</style>
      {/* Header */}
      <div data-testid="crm-header" className="crm-header" style={{ background: '#000', borderBottom: '3px solid #FFD700' }}>
        <div className="crm-header-left">
          <h1 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', color: '#FFD700', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Owner Pipeline</h1>
          <div style={{ padding: '4px 10px', background: '#FFD700', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.8rem', color: '#000' }}>{totalDeals}</div>
          <div data-testid="arr-mrr-toggle" onClick={() => setShowARR(prev => !prev)}
            style={{ padding: '4px 10px', background: '#6A0DAD', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.8rem', color: '#fff', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
            {showARR ? `ARR $${totalARR.toLocaleString()}` : `MRR $${totalMRR.toLocaleString()}`}
          </div>
          <a data-testid="demo-calendly-btn" href="https://calendly.com/hungrmates/mobile-app-consultation" target="_blank" rel="noopener noreferrer"
            style={{ padding: '4px 10px', background: '#007AFF', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.8rem', color: '#fff', textDecoration: 'none', whiteSpace: 'nowrap' }}>
            Demo
          </a>
        </div>
        <div className="crm-header-right">
          <select data-testid="call-stage-select" value={callSourceStage} onChange={(e) => setCallSourceStage(e.target.value)}
            style={{ padding: '6px 8px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.75rem', fontWeight: 700, outline: 'none', minWidth: 0 }}>
            {STAGES.map(s => <option key={s} value={s}>{s} ({deals.filter(d => d.stage === s).length})</option>)}
          </select>
          <button data-testid="start-calling-btn" onClick={() => startCallMode(callSourceStage)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '6px 12px', background: '#22c55e', color: '#000', border: '2px solid #000', borderRadius: '6px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <PhoneCall size={14} /> Call
          </button>
          <button data-testid="import-btn" onClick={() => setShowImport(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '6px 12px', background: '#6A0DAD', color: '#fff', border: '2px solid #000', borderRadius: '6px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <FileSpreadsheet size={14} /> Import
          </button>
          <button data-testid="add-deal-btn" onClick={() => setShowAdd(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '6px 12px', background: '#FFD700', color: '#000', border: '2px solid #000', borderRadius: '6px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            <Plus size={14} strokeWidth={3} /> New
          </button>
          <button onClick={() => { setToken(null); sessionStorage.removeItem('admin_token'); }}
            style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.1)', color: '#fff', border: '2px solid rgba(255,255,255,0.2)', borderRadius: '6px', cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase' }}>Logout</button>
          <a href="/" style={{ padding: '6px 10px', background: 'rgba(255,59,48,0.15)', color: '#FF3B30', border: '2px solid #FF3B30', borderRadius: '6px', fontWeight: 700, fontSize: '0.7rem', textTransform: 'uppercase', textDecoration: 'none' }}>Exit</a>
        </div>
      </div>

      {/* Daily Leads / Call Value / Goal Tracker Bar */}
      <div data-testid="daily-leads-bar" className="crm-stats-bar" style={{ background: '#111', borderBottom: '2px solid #333' }}>
        <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#aaa', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Leads:</span>
        <input data-testid="daily-leads-input" type="number" value={dailyLeads} onChange={(e) => setDailyLeads(e.target.value)} placeholder="0"
          style={{ width: '65px', padding: '5px 8px', background: '#000', border: '2px solid #FFD700', borderRadius: '6px', color: '#FFD700', fontSize: '1rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
        {dailyLeads && parseInt(dailyLeads) > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: '#555' }}>&times;</span>
            <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#aaa' }}>3,564</span>
            <span style={{ color: '#555' }}>=</span>
            <div style={{ padding: '4px 10px', background: '#FFD700', border: '2px solid #000', borderRadius: '6px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#000' }}>
              {(parseInt(dailyLeads) * 3564).toLocaleString()}
            </div>
          </div>
        )}
        <div className="divider" style={{ width: '2px', height: '24px', background: '#333' }} />
        <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#aaa', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>$/Owner Call:</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
          <span style={{ color: '#22c55e', fontWeight: 800 }}>$</span>
          <input data-testid="call-value-input" type="number" step="0.01" value={callValue}
            onChange={(e) => { setCallValue(e.target.value); sessionStorage.setItem('call_value', e.target.value); }}
            placeholder="0.00"
            style={{ width: '70px', padding: '5px 8px', background: '#000', border: '2px solid #22c55e', borderRadius: '6px', color: '#22c55e', fontSize: '1rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
        </div>
        <span style={{ color: '#555' }}>&times;</span>
        <div data-testid="dashboard-call-count" style={{ padding: '4px 10px', background: '#000', border: '2px solid #FFD700', borderRadius: '6px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#FFD700' }}>
          {callsMade}
        </div>
        <span style={{ color: '#555' }}>=</span>
        <div data-testid="dashboard-call-earnings" style={{ padding: '4px 12px', background: '#22c55e', border: '2px solid #000', borderRadius: '6px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#000' }}>
          ${(callsMade * parseFloat(callValue || '0')).toFixed(2)}
        </div>
        {callsMade > 0 && (
          <button data-testid="reset-call-counter" onClick={resetCallCounter} title="Reset call counter"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#555' }}>
            <X size={14} />
          </button>
        )}
        <div className="crm-goal-section" style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
          <div className="divider" style={{ width: '2px', height: '24px', background: '#333' }} />
          <Target size={14} style={{ color: '#FF8C00' }} />
          <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#FF8C00', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Goal:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
            <span style={{ color: '#FF8C00', fontWeight: 800 }}>$</span>
            <input data-testid="goal-revenue-input" type="number" value={goalRevenue}
              onChange={(e) => { setGoalRevenue(e.target.value); sessionStorage.setItem('goal_revenue', e.target.value); }}
              placeholder="500K"
              style={{ width: '90px', padding: '5px 8px', background: '#000', border: '2px solid #FF8C00', borderRadius: '6px', color: '#FF8C00', fontSize: '1rem', fontFamily: "'Bangers', cursive", outline: 'none', textAlign: 'center' }} />
          </div>
          <span style={{ fontWeight: 800, fontSize: '0.8rem', color: '#aaa', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>by:</span>
          <input data-testid="goal-deadline-input" type="date" value={goalDeadline}
            onChange={(e) => { setGoalDeadline(e.target.value); sessionStorage.setItem('goal_deadline', e.target.value); }}
            style={{ padding: '4px 6px', background: '#000', border: '2px solid #FF8C00', borderRadius: '6px', color: '#FF8C00', fontSize: '0.75rem', fontWeight: 700, outline: 'none' }} />
          {goalDeadline && (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const deadline = new Date(goalDeadline + 'T00:00:00');
            if (isNaN(deadline.getTime())) return null;
            let daysLeft = 0;
            if (excludeWeekends) {
              const d = new Date(today);
              while (d <= deadline) { const dow = d.getDay(); if (dow !== 0 && dow !== 6) daysLeft++; d.setDate(d.getDate() + 1); }
            } else {
              daysLeft = Math.max(0, Math.ceil((deadline - today) / 86400000));
            }
            const urgentColor = daysLeft <= 7 ? '#FF3B30' : daysLeft <= 30 ? '#FF8C00' : '#22c55e';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ padding: '4px 8px', background: `${urgentColor}15`, border: `1px solid ${urgentColor}`, borderRadius: '4px', fontWeight: 800, fontSize: '0.8rem', color: urgentColor, whiteSpace: 'nowrap' }}>
                  {daysLeft} {excludeWeekends ? 'biz' : ''} day{daysLeft !== 1 ? 's' : ''} left
                </div>
                <button data-testid="toggle-weekends"
                  onClick={() => { const next = !excludeWeekends; setExcludeWeekends(next); sessionStorage.setItem('exclude_weekends', String(next)); }}
                  title={excludeWeekends ? 'Showing business days — click for all days' : 'Showing all days — click for business days only'}
                  style={{ padding: '2px 6px', background: excludeWeekends ? '#FF8C0020' : '#333', border: `1px solid ${excludeWeekends ? '#FF8C00' : '#555'}`, borderRadius: '4px', color: excludeWeekends ? '#FF8C00' : '#888', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {excludeWeekends ? 'M-F' : '7d'}
                </button>
              </div>
            );
          })()}
          {goalRevenue && parseFloat(goalRevenue) > 0 && parseFloat(callValue || '0') > 0 && (() => {
            const goal = parseFloat(goalRevenue);
            const cv = parseFloat(callValue);
            const callsNeeded = Math.ceil(goal / cv);
            const dealsNeeded = Math.ceil(goal / 3564);
            const totalLoggedCalls = callLog.reduce((s, e) => s + (e.calls || 0), 0) + parseInt(todayCalls || '0');
            const callsLeft = Math.max(0, callsNeeded - totalLoggedCalls);
            const earned = totalLoggedCalls * cv;
            const pct = Math.min(100, (totalLoggedCalls / callsNeeded) * 100);
            const milestones = [{ at: 25, icon: '1/4' }, { at: 50, icon: '1/2' }, { at: 75, icon: '3/4' }, { at: 100, icon: 'MAX' }];
            return (
              <>
                <span style={{ color: '#555', fontWeight: 800, fontSize: '0.8rem' }}>=</span>
                <div style={{ padding: '4px 10px', background: '#FF8C0015', border: '1px solid #FF8C00', borderRadius: '4px', color: '#FF8C00', fontSize: '0.8rem', fontWeight: 700 }}>{callsNeeded.toLocaleString()} calls</div>
                <div style={{ padding: '4px 10px', background: '#6A0DAD15', border: '1px solid #6A0DAD', borderRadius: '4px', color: '#b57aff', fontSize: '0.8rem', fontWeight: 700 }}>{dealsNeeded.toLocaleString()} deals</div>
                <div style={{ width: '2px', height: '28px', background: '#333' }} />
                <span style={{ fontWeight: 800, fontSize: '0.85rem', color: '#aaa', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Today:</span>
                <input data-testid="today-calls-input" type="number" value={todayCalls}
                  onChange={(e) => { setTodayCalls(e.target.value); sessionStorage.setItem('today_calls', e.target.value); }}
                  placeholder="0" style={{ width: '80px', padding: '6px 10px', background: '#000', border: '2px solid #007AFF', borderRadius: '6px', color: '#007AFF', fontSize: '1.1rem', fontFamily: "'Bangers', cursive", letterSpacing: '0.03em', outline: 'none', textAlign: 'center' }} />
                <button data-testid="log-today-btn" onClick={() => {
                  const tc = parseInt(todayCalls || '0');
                  if (tc <= 0) return;
                  const today = new Date().toLocaleDateString();
                  const updated = [...callLog];
                  const existing = updated.find(e => e.date === today);
                  if (existing) { existing.calls += tc; } else { updated.push({ date: today, calls: tc }); }
                  setCallLog(updated); localStorage.setItem('call_log', JSON.stringify(updated));
                  setTodayCalls(''); sessionStorage.setItem('today_calls', '');
                }} style={{ padding: '6px 12px', background: '#007AFF', color: '#fff', border: '2px solid #000', borderRadius: '6px', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>+ Log</button>
                <span style={{ color: '#555', fontWeight: 800 }}>=</span>
                <div style={{ padding: '6px 14px', background: '#22c55e', border: '2px solid #000', borderRadius: '6px', fontFamily: "'Bangers', cursive", fontSize: '1.15rem', color: '#000', letterSpacing: '0.03em' }}>
                  ${earned.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div style={{ padding: '4px 10px', border: `2px solid ${callsLeft === 0 ? '#22c55e' : '#FF8C00'}`, background: callsLeft === 0 ? '#22c55e15' : '#FF8C0015', borderRadius: '4px', fontWeight: 800, fontSize: '0.8rem', color: callsLeft === 0 ? '#22c55e' : '#FF8C00' }}>
                  {callsLeft === 0 ? 'GOAL HIT!' : `${callsLeft.toLocaleString()} left`}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {milestones.map(m => {
                    const hit = pct >= m.at;
                    return (
                      <div key={m.at} title={`${m.at}% milestone${hit ? ' - REACHED!' : ''}`} style={{
                        width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: hit ? (m.at === 100 ? '#FFD700' : '#22c55e') : '#222',
                        border: `2px solid ${hit ? (m.at === 100 ? '#FFD700' : '#22c55e') : '#444'}`,
                        fontSize: '0.55rem', fontWeight: 800, color: hit ? '#000' : '#555', transition: 'all 0.3s',
                      }}>{m.at === 100 ? <Trophy size={12} /> : m.icon}</div>
                    );
                  })}
                </div>
                <div style={{ width: '80px', height: '8px', background: '#222', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', borderRadius: '4px', background: pct >= 100 ? '#22c55e' : pct >= 50 ? '#FF8C00' : '#FF3B30', transition: 'width 0.3s' }} />
                </div>
                {totalLoggedCalls > 0 && <div style={{ padding: '3px 8px', background: '#111', border: '1px solid #333', borderRadius: '4px', color: '#aaa', fontSize: '0.7rem', fontWeight: 700 }}>{totalLoggedCalls.toLocaleString()} total</div>}
                {callLog.length > 0 && (
                  <button data-testid="reset-call-log" onClick={() => { setCallLog([]); localStorage.setItem('call_log', '[]'); }} title="Reset cumulative log"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: '#555', transition: 'color 0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FF3B30'} onMouseLeave={(e) => e.currentTarget.style.color = '#555'}><X size={14} /></button>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="crm-filter-bar" style={{ background: '#0f0f0f', borderBottom: '2px solid #222' }}>
        <span style={{ fontWeight: 800, fontSize: '0.75rem', color: '#666', textTransform: 'uppercase' }}>Filter:</span>
        <div data-testid="deal-search" style={{ position: 'relative', display: 'flex', alignItems: 'center', flex: '1 1 150px', maxWidth: '200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '10px', color: '#666' }} />
          <input data-testid="deal-search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search..."
            style={{ padding: '6px 10px 6px 28px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.8rem', fontWeight: 600, outline: 'none', width: '100%' }}
            onFocus={(e) => { e.target.style.borderColor = '#FFD700'; }} onBlur={(e) => { e.target.style.borderColor = '#333'; }} />
          {searchQuery && (
            <button data-testid="deal-search-clear" onClick={() => setSearchQuery('')}
              style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: '2px', display: 'flex' }}><X size={14} /></button>
          )}
        </div>
        <select data-testid="state-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
          style={{ padding: '6px 10px', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '0.85rem', fontWeight: 600, outline: 'none' }}>
          <option value="">All States</option>
          {allStates.map(s => <option key={s} value={s}>{s} ({deals.filter(d => d.state === s).length})</option>)}
        </select>
        {stateFilter && <span style={{ padding: '4px 10px', background: '#FFD70020', border: '1px solid #FFD700', borderRadius: '4px', color: '#FFD700', fontSize: '0.8rem', fontWeight: 700 }}>Showing: {stateFilter}</span>}
        {searchQuery && <span data-testid="deal-search-count" style={{ padding: '4px 10px', background: '#6A0DAD20', border: '1px solid #6A0DAD', borderRadius: '4px', color: '#b57aff', fontSize: '0.8rem', fontWeight: 700 }}>{filteredDeals.length} result{filteredDeals.length !== 1 ? 's' : ''}</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button data-testid="toggle-chart-btn" onClick={() => setShowChart(prev => !prev)}
            style={{ padding: '5px 12px', background: showChart ? '#6A0DAD20' : 'rgba(255,255,255,0.05)', border: `2px solid ${showChart ? '#6A0DAD' : '#444'}`, borderRadius: '6px', color: showChart ? '#b57aff' : '#aaa', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <BarChart3 size={14} /> Chart
          </button>
          <button onClick={() => setShowAddStage(true)}
            style={{ padding: '5px 12px', background: 'rgba(255,255,255,0.05)', border: '2px solid #444', borderRadius: '6px', color: '#aaa', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}>+ Add Column</button>
        </div>
      </div>

      {showChart && <PipelineChart STAGES={STAGES} STAGE_COLORS={STAGE_COLORS} getStageDeals={getStageDeals} getStageTotal={getStageTotal} />}

      <KanbanBoard
        STAGES={STAGES} STAGE_COLORS={STAGE_COLORS} getStageDeals={getStageDeals} getStageTotal={getStageTotal}
        dragDeal={dragDeal} handleDragStart={handleDragStart} handleDragOver={handleDragOver} handleDrop={handleDrop}
        handleTouchStart={handleTouchStart} handleTouchEnd={handleTouchEnd} quickMove={quickMove} moveColumn={moveColumn}
        setEditDeal={setEditDeal} setShowStageEdit={setShowStageEdit} setStageEditName={setStageEditName} setStageEditColor={setStageEditColor}
        exportStageCSV={exportStageCSV} handleDeleteStageDeals={handleDeleteStageDeals}
        onStampEmail={stampEmail} onMoveGroup={moveGroup}
        onMoveDealInColumn={moveDealInColumn}
      />

      <WeeklyCalendar apiUrl={API_URL} token={token} deals={deals} />

      <USAMap apiUrl={API_URL} token={token} />

      <PlanCalculator />

      {showAdd && <AddDealModal newDeal={newDeal} setNewDeal={setNewDeal} STAGES={STAGES} handleAddDeal={handleAddDeal} onClose={() => setShowAdd(false)} />}
      {editDeal && <EditDealModal editDeal={editDeal} setEditDeal={setEditDeal} STAGES={STAGES} handleUpdateDeal={handleUpdateDeal} handleDeleteDeal={handleDeleteDeal} />}
      {showImport && <ImportModal STAGES={STAGES} importStep={importStep} sheetUrl={sheetUrl} setSheetUrl={setSheetUrl} sheetPreview={sheetPreview}
        columnMap={columnMap} setColumnMap={setColumnMap} importStage={importStage} setImportStage={setImportStage}
        importResult={importResult} importError={importError} overrideState={overrideState} setOverrideState={setOverrideState}
        overrideDealValue={overrideDealValue} setOverrideDealValue={setOverrideDealValue}
        handlePreviewSheet={handlePreviewSheet} handleImportSheet={handleImportSheet} resetImport={resetImport} />}
      {showStageEdit && <EditStageModal showStageEdit={showStageEdit} setShowStageEdit={setShowStageEdit}
        stageEditName={stageEditName} setStageEditName={setStageEditName} stageEditColor={stageEditColor} setStageEditColor={setStageEditColor}
        handleUpdateStage={handleUpdateStage} handleDeleteStage={handleDeleteStage} />}
      {showAddStage && <AddStageModal showAddStage={showAddStage} setShowAddStage={setShowAddStage}
        newStageName={newStageName} setNewStageName={setNewStageName} newStageColor={newStageColor} setNewStageColor={setNewStageColor} handleAddStage={handleAddStage} />}
      {callMode && <CallingMode
        callsMade={callsMade} callValue={callValue} goalRevenue={goalRevenue}
        goalDeadline={goalDeadline}
        exitCallMode={exitCallMode} setCallsMade={setCallsMade}
        apiUrl={API_URL} token={token} />}
    </div>
  );
};
